import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const navigate = useNavigate();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6mh4k94a3uojp5fgo0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToConsultancy = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('consultancy-services');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  return (
    <footer className="bg-gray-50 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-5 md:px-6">
        <div className="flex flex-col md:flex-row gap-10 md:gap-16">
          <div className="w-full md:w-2/5 md:pr-12">
            <h3 className="text-2xl md:text-4xl font-semibold text-gray-900 mb-4 md:mb-8 leading-tight">
              Stay Connected with Hengtong
            </h3>

            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              Subscribe to receive updates on our latest solutions, industry insights, and exclusive offers tailored for your business growth.
            </p>

            <form id="newsletter-form" data-readdy-form onSubmit={handleSubscribe}>
              <label htmlFor="footer-email" className="block text-xs uppercase text-gray-500 mb-2">
                Email Address
              </label>
              <div className="flex items-center border-b border-gray-300 focus-within:border-gray-900 transition-colors">
                <input
                  type="email"
                  id="footer-email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent py-3 md:py-4 text-gray-900 outline-none text-sm placeholder-gray-400"
                  placeholder="your@email.com"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="whitespace-nowrap ml-3 px-3 py-1.5 md:px-4 md:py-2 bg-gray-900 text-white text-xs font-medium rounded-md hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Subscribe'}
                </button>
              </div>

              {submitStatus === 'success' && (
                <p className="text-green-600 text-xs mt-2">Successfully subscribed!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-600 text-xs mt-2">Something went wrong. Please try again.</p>
              )}
            </form>
          </div>

          <div className="w-full md:w-3/5 grid grid-cols-3 gap-6 md:gap-12">
            <div>
              <h4 className="text-xs md:text-sm font-semibold text-gray-900 mb-4 md:mb-6">IT Services</h4>
              <ul className="space-y-3 md:space-y-4">
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-xs md:text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-left">
                    IT Solutions
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-xs md:text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-left">
                    Cloud Services
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-xs md:text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-left">
                    System Integration
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-xs md:text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-left">
                    Tech Support
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-xs md:text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-left">
                    Digital Transformation
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs md:text-sm font-semibold text-gray-900 mb-4 md:mb-6">Consultancy Services</h4>
              <ul className="space-y-3 md:space-y-4">
                <li>
                  <button onClick={goToConsultancy} className="text-xs md:text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-left">
                    Strategic Planning
                  </button>
                </li>
                <li>
                  <button onClick={goToConsultancy} className="text-xs md:text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-left">
                    Business Advisory
                  </button>
                </li>
                <li>
                  <button onClick={goToConsultancy} className="text-xs md:text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-left">
                    Operations Mgmt
                  </button>
                </li>
                <li>
                  <button onClick={goToConsultancy} className="text-xs md:text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-left">
                    Risk Assessment
                  </button>
                </li>
                <li>
                  <button onClick={goToConsultancy} className="text-xs md:text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-left">
                    Compliance
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs md:text-sm font-semibold text-gray-900 mb-4 md:mb-6">Company</h4>
              <ul className="space-y-3 md:space-y-4">
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-xs md:text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-left">
                    About Us
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-xs md:text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-left">
                    Contact
                  </button>
                </li>
                <li>
                  <a
                    href="mailto:contact@hengtong.online"
                    className="hover:text-blue-700 transition-colors cursor-pointer flex items-start gap-1"
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-gray-900 mt-0.5 shrink-0">
                      <i className="ri-mail-line text-xs md:text-sm"></i>
                    </span>
                    <span className="text-blue-600 text-xs md:text-sm break-all">contact@hengtong.online</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-10 md:mt-16 pt-6 md:pt-8 border-t border-gray-200 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
          <p className="text-xs md:text-sm text-gray-500 text-center md:text-left">
            © 2025 Hengtong Consultant & Solution. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}